import { useSelectionStore } from "../state/useSelectionStore";
import { useLayerStore } from "../state/useLayerStore";
import { PRICE_YEARS, countField, priceField } from "../map/layers/prices";
import { num, pctChange, pricePerM2 } from "../lib/format";
import { PriceChart } from "./PriceChart";
import { NearbyStops } from "./NearbyStops";
import { useShortlistStore } from "../state/useShortlistStore";
import { useSite } from "../lib/useSite";
import { CLAY_LABELS } from "../lib/risks";
import { HEAT_LABELS } from "../lib/climate";
import { Breadcrumb } from "./Breadcrumb";
import { levelDef } from "../map/levels";

/** Details for whatever zone was last clicked. */
const ENV_LABELS = [
  "",
  "within WHO guidelines",
  "above WHO guidelines",
  "above EU limit values",
];
const ENV_COLORS = ["", "#cfe6d4", "#f6c445", "#cf3a2e"];

function EnvRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline gap-2 text-[12px]">
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-sm"
        style={{ background: ENV_COLORS[value] ?? "#ccc" }}
      />
      <span className="w-20 shrink-0">{label}</span>
      <span className="text-[11px] text-ink-soft">
        {ENV_LABELS[value] ?? "no data"}
      </span>
    </div>
  );
}

export function DetailPanel() {
  const selection = useSelectionStore((s) => s.selection);
  // Split in two so the body can use hooks that need a selection to exist,
  // and so it remounts cleanly when a different zone is picked.
  return selection ? (
    <PanelBody key={`${selection.code}:${selection.point.lon}`} />
  ) : null;
}

function PanelBody() {
  const selection = useSelectionStore((s) => s.selection)!;
  const select = useSelectionStore((s) => s.select);
  const year = useLayerStore((s) => s.year);
  const addCandidate = useShortlistStore((s) => s.add);
  const { data: siteData } = useSite(selection.point.lon, selection.point.lat);
  const green = siteData?.green ?? null;
  const airNoise = siteData?.air_noise ?? null;
  const clay = siteData?.clay ?? null;
  const heat = siteData?.heat_island ?? null;

  const p = selection.properties;
  const asNum = (k: string) => {
    const v = p[k];
    return typeof v === "number" ? v : null;
  };

  const median = asNum(priceField(year));
  const count = asNum(countField(year));
  const first = PRICE_YEARS.map((y) => asNum(priceField(y))).find((v) => v !== null);
  const firstYear = PRICE_YEARS.find((y) => asNum(priceField(y)) !== null);
  const latest = asNum("p_latest");
  const latestYear = asNum("year_latest");

  return (
    <aside className="absolute right-4 top-4 z-10 w-80 overflow-hidden rounded-xl border border-line bg-surface/95 shadow-lg backdrop-blur">
      <header className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold">{selection.name}</h2>
          <p className="mt-0.5 text-[11px] text-ink-soft">
            {levelDef(selection.scope).noun} · {selection.code}
          </p>
          <div className="mt-1.5">
            <Breadcrumb />
          </div>
        </div>
        <button
          onClick={() => select(null)}
          className="-mr-1 -mt-1 rounded p-1 text-ink-soft hover:bg-surface-alt"
          aria-label="Close"
        >
          ✕
        </button>
      </header>

      <div className="px-4 py-3">
        {median !== null ? (
          <>
            <div className="text-2xl font-semibold tabular-nums">
              {pricePerM2(median)}
            </div>
            <p className="mt-0.5 text-[11px] text-ink-soft">
              median of {num(count ?? 0)} sale{count === 1 ? "" : "s"} in {year}
            </p>
          </>
        ) : latest !== null ? (
          <>
            <div className="text-2xl font-semibold tabular-nums text-ink-soft">
              {pricePerM2(latest)}
            </div>
            <p className="mt-0.5 text-[11px] text-ink-soft">
              {year} too thin — showing {latestYear}
            </p>
          </>
        ) : (
          <p className="text-[13px] text-ink-soft">
            Fewer than 5 recorded sales — no median published.
          </p>
        )}

        {first !== undefined && first !== null && latest !== null && firstYear !== year && (
          <div className="mt-3 flex items-baseline gap-2 border-t border-line pt-3">
            <span className="text-sm font-medium tabular-nums">
              {pctChange(first, latest)}
            </span>
            <span className="text-[11px] text-ink-soft">
              since {firstYear}
            </span>
          </div>
        )}

        <div className="mt-3 border-t border-line pt-3">
          <h3 className="mb-1 text-[11px] font-medium uppercase tracking-wider text-ink-soft">
            Price trend
          </h3>
          <PriceChart scope={selection.scope} code={selection.code} />
        </div>

        {airNoise && (
          <div className="mt-3 border-t border-line pt-3">
            <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-ink-soft">
              Air & noise
            </h3>
            <div className="space-y-1">
              <EnvRow label="Air quality" value={airNoise.air} />
              <EnvRow label="Noise" value={airNoise.noise} />
            </div>
          </div>
        )}

        {green && (
          <div className="mt-3 border-t border-line pt-3">
            <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-ink-soft">
              Green index
            </h3>
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-semibold tabular-nums">
                {green.score}
              </span>
              <span className="text-[11px] text-ink-soft">/ 100</span>
            </div>
            <p className="mt-0.5 text-[11px] text-ink-soft">
              {green.green_pct}% green ·{" "}
              {green.park_m < 1000
                ? `park ${green.park_m} m`
                : "no park within 1 km"}
            </p>
          </div>
        )}

        {/*
          One line each, and no more. The depth belongs in the Risks and
          Climate panes; this card is already tall, and what it owes the reader
          is a reason to go and look.
        */}
        {(clay || (heat && heat.day_class > 0)) && (
          <div className="mt-3 border-t border-line pt-3">
            <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-ink-soft">
              Ground & heat
            </h3>
            <div className="space-y-1">
              {clay && (
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[11px] text-ink-soft">Clay shrink-swell</span>
                  <span className="text-[11px] font-medium">
                    {CLAY_LABELS[clay.niveau] ?? clay.alea}
                  </span>
                </div>
              )}
              {heat && heat.day_class > 0 && (
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[11px] text-ink-soft">Heat island, day / night</span>
                  <span className="text-[11px] font-medium">
                    {HEAT_LABELS[heat.day_class]} / {HEAT_LABELS[heat.night_class]}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-3 border-t border-line pt-3">
          <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-ink-soft">
            Walkable from here
          </h3>
          <NearbyStops lon={selection.point.lon} lat={selection.point.lat} />
        </div>

        <button
          onClick={() =>
            addCandidate({
              label: selection.address ?? selection.name,
              lon: selection.point.lon,
              lat: selection.point.lat,
              localMedian: median ?? latest ?? undefined,
              green: green?.score,
              air: airNoise?.air,
              noise: airNoise?.noise,
            })
          }
          className="mt-3 w-full rounded-lg border border-line bg-surface-alt py-1.5 text-[12px] font-medium hover:border-accent hover:text-accent"
        >
          Save to shortlist
        </button>

        <table className="mt-3 w-full border-t border-line pt-2 text-[11px]">
          <tbody>
            {PRICE_YEARS.map((y) => {
              const v = asNum(priceField(y));
              const n = asNum(countField(y));
              return (
                <tr key={y} className={y === year ? "font-medium" : "text-ink-soft"}>
                  <td className="py-0.5 tabular-nums">{y}</td>
                  <td className="py-0.5 text-right tabular-nums">
                    {v !== null ? pricePerM2(v) : "—"}
                  </td>
                  <td className="py-0.5 pl-2 text-right tabular-nums text-ink-soft">
                    {n !== null ? `${num(n)} sales` : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </aside>
  );
}
